import dayjs from 'dayjs';
import {t} from 'i18next';
import {cloneDeep} from 'lodash';
import {useLocation, useSearchParams} from 'react-router-dom';
import {LeftPanelReportResponse} from '../../../../../api/types/reportingApi.type';
import {isEmpty} from '../../../../../utils/formatters';
import {buildTreeItems} from '../../../../../utils/reportUtils';
import {
  AccordionSection,
  ColumnConfig,
  FilterComponent,
  Reports,
  Section,
} from '../../config/reportTypes';
import utc from 'dayjs/plugin/utc';

type TreeItem = {
  id: string;
  fileType: string;
  children: TreeItem[];
};

type ReviewerList = LeftPanelReportResponse['reviewerList'];
type Reviewer = ReviewerList[number];

const getFolderIds = (arr: TreeItem[]): string[] => {
  let folderIds: string[] = [];

  arr.forEach((item) => {
    if (item.fileType === 'folder') {
      folderIds.push(item.id);
      if (item.children && item.children.length > 0) {
        folderIds = folderIds.concat(getFolderIds(item.children));
      }
    }
  });

  return folderIds;
};

export const useReviewerProductivityLocationParams = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const {item, reportId, isBackgroundReport} = location.state ?? {};
  const urlParamReportId = Number.parseInt(searchParams.get('ReportID') ?? '0');
  const urlParamReportName = searchParams.get('ReportLabel');

  return {
    isBackgroundReport,
    urlReportId: reportId ?? urlParamReportId,
    reportLabel: item?.label ?? urlParamReportName,
  };
};

export const mergeApiDataIntoConfig = (
  report: Reports,
  apiData: LeftPanelReportResponse,
  savedReportData: Record<string, any> = {},
  initialSelectedFilter: Record<string, any> = {}
) => {
  dayjs.extend(utc);

  const config = report.config;

  const {selectedReviewers = [], fromDateTime, toDateTime} = savedReportData;

  const applyButtonDisabled =
    isEmpty(selectedReviewers) && isEmpty(fromDateTime) && isEmpty(toDateTime);

  const resetButtonDisabled =
    isEmpty(selectedReviewers) && isEmpty(fromDateTime) && isEmpty(toDateTime);

  const mapReviwerList = (reviewer: Reviewer, index: number) => ({
    label: reviewer.reviewer_Name,
    // eslint-disable-next-line security/detect-object-injection
    value: selectedReviewers?.[index] ?? false,
  });

  const mapSelectedAssignments = (id: number) => id?.toString();

  const configObject = {
    sections: config.sections.map((section: Section) => ({
      ...section,
      filters: section.filters.map((filter: AccordionSection) => {
        return {
          ...filter,
          components: filter.components.map((comp: FilterComponent) => {
            if (
              comp.input_type === 'checkboxList' &&
              filter.id === 'reviewerList'
            ) {
              const transformedData = (apiData?.reviewerList ?? []).map(
                mapReviwerList
              );
              return {
                ...comp,
                childLabels: transformedData,
              };
            } else if (
              comp.input_type === 'multiSelectTreeViewList' &&
              filter.id === 'lstAssignmentGroup'
            ) {
              const transformedData = buildTreeItems(
                apiData?.lstAssignmentGroup ?? []
              );
              return {
                ...comp,
                treeData: transformedData,
                defaultExpandedIds: getFolderIds(transformedData),
                initialValues: (savedReportData?.selectedAssignments ?? []).map(
                  mapSelectedAssignments
                ),
              };
            } else if (
              comp.input_type === 'datePicker' &&
              filter.id === 'dateRangeSection'
            ) {
              let initialValue;

              if (initialSelectedFilter) {
                initialValue = initialSelectedFilter[comp.key]?.split(' ')?.[0];
              }

              if (savedReportData?.[comp.key]) {
                initialValue = savedReportData?.[comp.key]?.split(' ')?.[0];
              }

              const initialStartDate = initialValue
                ? dayjs.utc(initialValue).format('YYYY/MM/DD')
                : undefined;

              return {
                ...comp,
                maxDateTime: dayjs.utc(),
                initialStartDate,
              };
            }
            return {
              ...comp,
            };
          }),
        };
      }),
    })),
  };

  return {
    ...report,
    config: {
      selectionCriteria: {
        ...config.selectionCriteria,
        actions: config.selectionCriteria.actions.map((action) => {
          if (
            action.input_type === 'button' &&
            action.actionKey === 'applyChanges'
          ) {
            return {
              ...action,
              disabled: applyButtonDisabled,
            };
          } else if (
            action.input_type === 'button' &&
            action.actionKey === 'reset'
          ) {
            return {
              ...action,
              disabled: resetButtonDisabled,
            };
          } else {
            return action;
          }
        }),
      },
      sections: cloneDeep(configObject?.sections),
    },
  };
};

export const getSelectedReviewer = (
  filterValues: Record<string, any>,
  savedReportData?: any
): number[] => {
  const selectedValues = filterValues?.selectedReviewers ?? [];
  const reviewersList = savedReportData?.reviewerList ?? [];

  const selectedReviewerIDs = selectedValues
    .map((isSelected: boolean, index: number) =>
      // eslint-disable-next-line security/detect-object-injection
      isSelected ? reviewersList[index]?.reviewerID : null
    )
    .filter(Boolean);

  return selectedReviewerIDs;
};

const TABLE_BASE_WIDTH: number = 1044;

export const reviewerProductivityColumnConfig: ColumnConfig[] = [
  {
    field: 'reviewerName',
    headerName: t('reports.reviewerProductivity.reportGrid.reviewer'),
    flex: 174 / TABLE_BASE_WIDTH,
    minWidth: 174,
    isVisible: true,
  },
  {
    field: 'distributedDocs',
    headerName: t('reports.reviewerProductivity.reportGrid.distributedDocs'),
    flex: 174 / TABLE_BASE_WIDTH,
    minWidth: 174,
    isVisible: true,
  },
  {
    field: 'completedDocs',
    headerName: t('reports.reviewerProductivity.reportGrid.completedDocs'),
    flex: 174 / TABLE_BASE_WIDTH,
    minWidth: 174,
    isVisible: true,
  },
  {
    field: 'documentsCompletedBythisReviewer',
    headerName: t(
      'reports.reviewerProductivity.reportGrid.documentsCompletedBythisReviewer'
    ),
    flex: 174 / TABLE_BASE_WIDTH,
    minWidth: 174,
    isVisible: true,
  },
  {
    field: 'reviewedDocsPerHour',
    headerName: t(
      'reports.reviewerProductivity.reportGrid.reviewedDocsPerHour'
    ),
    flex: 174 / TABLE_BASE_WIDTH,
    minWidth: 174,
    isVisible: true,
  },
  {
    field: 'variance',
    headerName: t('reports.reviewerProductivity.reportGrid.variance'),
    flex: 174 / TABLE_BASE_WIDTH,
    minWidth: 174,
    isVisible: true,
  },
];
